import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import EventForm from "../modal/form";
import { useGetEventsQuery } from "@/services/eventApi";
import TableFilters from "@/components/ui/table/data-table-filter";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import { Event } from "@/types/event";
import { displayValue } from "@/lib/helper";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { EventBookingType, EventStatus } from "@/types/enums";
import { Badge } from "@/components/ui/badge";

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  type: string;
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "events",
};

function EventsTableView() {
  const event = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).event ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [data, setData] = useState<Event | undefined>(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  // search input
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});

  const {
    data: eventData,
    isLoading,
    refetch,
  } = useGetEventsQuery(
    { org_id: orgId, query: query },
    {
      skip: query == "",
    }
  );

  const eventstableData = React.useMemo(() => {
    return Array.isArray(eventData?.data) ? eventData?.data : [];
  }, [eventData]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const actionsColumn: ColumnDef<Event> = {
    accessorKey: "action",
    header: ({ table }) => <span>Action</span>,
    cell: ({ row }) => (
      <DataTableRowActions
        access={event}
        handleEdit={handleEdit}
        data={row.original}
        refetch={refetch}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  };

  const columns: ColumnDef<Event>[] = [
    {
      accessorKey: "name",
      meta: "Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Event Name"
          sortKey="name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="flex items-center gap-2">
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="capitalize cursor-pointer">
                      <span>
                        {displayValue(
                          `${row.original.name}`.length > 20
                            ? `${row.original.name}`.substring(0, 20) + "..."
                            : `${row.original.name}`
                        )}
                      </span>
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="capitalize text-sm">
                      {displayValue(`${row?.original?.name}`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    
    {
      accessorKey: "facility_name",
      meta: "Facility Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Facility Name"
          sortKey="facility_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.facility_name || "N/A"}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "booking_type",
      meta: "Booking Type",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Booking Type"
          sortKey="booking_type"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const bookingType = row.original.booking_type;
        const isFullyBooked = bookingType === EventBookingType.FullyBooked;
        return (
          // <Badge
          //   variant={isFullyBooked ? "default" : "secondary"}
          //   className={isFullyBooked ? "bg-blue-500" : "bg-orange-500"}
          // >
          //   {isFullyBooked ? "Fully" : "Partial"}
          // </Badge>
          <p className="capitalize">{isFullyBooked ? "Fully Booked" : "Partially Booked"}</p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "status",
      meta: "Status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const status = row.original.status || EventStatus.Upcoming;
        const getStatusBadge = () => {
          switch (status) {
            case EventStatus.Upcoming:
              return (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Upcoming
                </Badge>
              );
            case EventStatus.Ongoing:
              return (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Ongoing
                </Badge>
              );
            case EventStatus.Completed:
              return (
                <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                  Completed
                </Badge>
              );
            default:
              return (
                <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                  {status}
                </Badge>
              );
          }
        };
        // return getStatusBadge();
        return <p className="capitalize">{status}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "max_attendees",
      meta: "Max Attendees",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Max Attendees"
          sortKey="max_attendees"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.max_attendees}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "start_date",
      meta: "Start Date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Start Date"
          sortKey="start_date"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.start_date}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "end_date",
      meta: "End Date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="End Date"
          sortKey="end_date"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return <span>{row.original.end_date}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    ...(event !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: eventstableData as Event[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const handleOpen = () => {
    setAction("add");
    setIsDialogOpen(true);
  };

  const handleEdit = (data: Event) => {
    setAction("edit");
    setData(data);
    setIsDialogOpen(true);
  };

  const totalRecords = eventData?.filtered_counts ?? 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleFilterChange = (
    field: string,
    value: string | React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = typeof value === "string" ? value : value.target.value;

    setFilterData((prev) => {
      const newFilter = { ...prev };

      if (newValue.trim() === "") {
        delete newFilter[field];
      } else {
        newFilter[field] = newValue;
      }

      return newFilter;
    });
  };

  const filterDisplay = [
    {
      type: "date",
      name: "start_date",
      label: "Start Date",
      function: (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFilterChange("start_date", e),
    },
    {
      type: "date",
      name: "end_date",
      label: "End Date",
      function: (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFilterChange("end_date", e),
    },
  ];

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3">
        {/* Table Filter component */}
        <TableSearchFilter setSearchCriteria={setSearchCriteria} inputValue={inputValue} setInputValue={setInputValue} />

        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          {event !== "read" && (
            <Button
              className="bg-primary text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 px-2"
              onClick={handleOpen}
            >
              <PlusIcon className="size-4" />
              Create New
            </Button>
          )}
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      <div className="rounded-none border border-border  ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (event !== "read" && (e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') && (e.target === e.currentTarget || e.target.closest('td')))) {
                        handleEdit(row.original)
                      }
                    }}
                    className={event !== "read" ? "cursor-pointer hover:bg-secondary" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No events found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {eventstableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      <EventForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        data={data}
        setData={setData}
        refetch={refetch}
      />
    </div>
  );
}

export default EventsTableView;
